import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type {
  PostInsightsData,
  ProductReviewsSummary,
  TPostElement,
  TProductsElement,
} from '@shared/dream-components/src';
import structuredClone from '@ungap/structured-clone';

import { withLazyBlocks } from '~/components/DreamBuilder/lazyBlock';
import type { NodeContent } from '~/interfaces/page';
import type { Subscription } from '~/interfaces/subscriptions';
import { fetchPostInsights } from '~/services/fetchers/fetchPostInsights';
import { fetchProductReviewSummaries } from '~/services/fetchers/fetchProductReviewSummaries';
import type { StaticPageLoaderData } from '~/services/static_page';
import { findPostIdsPostNodes } from '~/utils/findPostIdsPostNodes';
import { findProductIdsProductNodes } from '~/utils/findProductIdsProductNodes';
import { findMultipleTipTapContentNodes, findTipTapContentNodes } from '~/utils/findTipTapContentNode';
import getCookieValue from '~/utils/getCookieValue';
import { shouldClipAfterContentGate } from '~/utils/shouldClipAfterContentGate';

const staticPageClientLoader = withLazyBlocks(async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<StaticPageLoaderData>();
  if (!serverData?.page) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }
  const content = structuredClone(serverData?.page?.viewable_page_version?.content);
  if (!content) {
    return serverData;
  }
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  // TODO: use findMultipleTipTapContentNodes here instead of calling findTipTapContentNodes multiple times
  const postNodes = findTipTapContentNodes(content as NodeContent, 'post');
  const productNodes = findTipTapContentNodes(content as NodeContent, 'products');

  const foundNodes = findMultipleTipTapContentNodes(content as NodeContent, ['contentGate']);
  const hasFoundNodes = Object.values(foundNodes || {}).some((node) => node !== null);

  if (!hasFoundNodes && postNodes.length === 0 && productNodes.length === 0) {
    return serverData;
  }

  const requests = [];

  const token = getCookieValue('token', document.cookie);
  if (foundNodes.contentGate && token) {
    const requestBody: { jwt_token: string; subscriber_id?: string } = { jwt_token: token };
    requests.push(
      fetch('/current_subscription', {
        method: 'POST',
        headers: {
          'Sec-Fetch-Dest': 'empty',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }).catch(() => null),
    );
  } else {
    requests.push(Promise.resolve(null));
  }

  let subscription: Subscription | undefined;
  let likedPosts: string[] = [];
  let postsInsights: Record<string, PostInsightsData> | undefined;
  let productReviewSummaries: Record<string, ProductReviewsSummary> | undefined;

  if (postNodes.length > 0) {
    const postIds = findPostIdsPostNodes(postNodes as TPostElement[]);
    if (postIds.length > 0) {
      requests.push(fetchPostInsights(postIds).catch(() => null));
      if (token) {
        requests.push(fetch('/liked_ids', { method: 'GET', headers: { 'Sec-Fetch-Dest': 'empty' } }).catch(() => null));
      } else {
        requests.push(Promise.resolve(null));
      }
    } else {
      requests.push(Promise.resolve(null));
      requests.push(Promise.resolve(null));
    }
  } else {
    requests.push(Promise.resolve(null));
    requests.push(Promise.resolve(null));
  }

  if (productNodes.length > 0) {
    const productIds = findProductIdsProductNodes(productNodes as unknown as TProductsElement[]);
    if (productIds.length > 0) {
      requests.push(fetchProductReviewSummaries(productIds).catch(() => null));
    } else {
      requests.push(Promise.resolve(null));
    }
  } else {
    requests.push(Promise.resolve(null));
  }

  const [subscriptionRes, postsInsightsRes, likedIdsRes, productReviewSummariesRes] = await Promise.all(requests);

  if (subscriptionRes && (subscriptionRes as Response).ok) {
    subscription = await (subscriptionRes as Response).json();
  }

  let shouldClipContent = false;
  try {
    if (foundNodes.contentGate) {
      shouldClipContent = shouldClipAfterContentGate({
        contentGateNode: foundNodes.contentGate,
        subscription,
        cookiesHeader: document.cookie,
        tierIds: searchParams.getAll('tier_ids[]'),
      });
    }
  } catch (error: unknown) {
    Sentry.captureException(error);
  }

  if (likedIdsRes && (likedIdsRes as Response).ok) {
    try {
      const likedIdsBody = await (likedIdsRes as Response).json();
      if (likedIdsBody && 'post_ids' in likedIdsBody) {
        likedPosts = likedIdsBody.post_ids;
      }
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  }

  if (postNodes.length > 0 && postsInsightsRes && (postsInsightsRes as Response).ok) {
    try {
      const postsInsightsBody = await (postsInsightsRes as Response).json();
      if ('posts' in postsInsightsBody) {
        postsInsights = postsInsightsBody.posts;
      }
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  }

  if (productNodes.length > 0 && productReviewSummariesRes && (productReviewSummariesRes as Response).ok) {
    try {
      const productReviewSummariesBody = await (productReviewSummariesRes as Response).json();
      if ('summaries' in productReviewSummariesBody) {
        productReviewSummaries = productReviewSummariesBody.summaries;
      }
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  }

  return {
    ...serverData,
    page: {
      ...serverData.page,
      viewable_page_version: {
        ...serverData.page?.viewable_page_version,
        content,
      },
    },
    hasPostInsightsDataFetched: true,
    hasLikedPostsDataFetched: true,
    hasProductReviewSummariesDataFetched: true,
    isContentClipped: shouldClipContent,
    subscription,
    postsInsights,
    likedPosts,
    productReviewSummaries,
  };
});

staticPageClientLoader.hydrate = true;

export { staticPageClientLoader };
